<script>
import { mapState, mapActions, mapGetters } from 'vuex';

export default {
  name: 'FerryRouteMapping',
  props: {
    shown: {
      required: true,
      type: Boolean,
    },
  },
  computed: {
    ...mapState({
      route: state => state.transportation.routes.route,
      routeAreas: state => state.transportation.routes.areas,
      routeAreasLoading: state => state.transportation.routes.areasLoading,
      routeAreasError: state => state.transportation.routes.areasError,
      countries: state => state.twenty.countries.items,
      countriesLoading: state => state.twenty.countries.itemsLoading,
      countriesError: state => state.twenty.countries.itemsError,
      areas: state => state.twenty.areas.items,
      areasLoading: state => state.twenty.areas.itemsLoading,
      areasError: state => state.twenty.areas.itemsError,
      saveLoading: state => state.transportation.routes.saveLoading,
      saveError: state => state.transportation.routes.saveError,
    }),
    ...mapGetters({
      countriesList: 'twenty/countries',
    }),
    sortedAreas() {
      if (this.areas.length) {
        const routeAreas = [];
        this.routeAreas.forEach((area) => {
          routeAreas[area.id] = !area.deleted;
        });
        return [...this.areas]
          .filter(area => !routeAreas[area.id])
          .sort((a, b) => (a.name > b.name ? 1 : -1));
      }
      return [];
    },
    sortedRouteAreas() {
      if (this.routeAreas.length) {
        return [...this.routeAreas]
          .filter((area) => {
            if (!this.search) {
              return true;
            }
            const searchIn = area.name + area.country;
            return searchIn.toLowerCase()
              .indexOf(this.search.toLowerCase()) !== -1;
          })
          .sort((a, b) => (a.name > b.name ? 1 : -1));
      }
      return [];
    },
    loading() {
      return this.routeAreasLoading ||
        this.areasLoading ||
        this.countriesLoading ||
        this.saveLoading;
    },
    changesMade() {
      if (this.routeAreas) {
        for (let i = 0; i < this.routeAreas.length; i += 1) {
          if (this.routeAreas[i].deleted || this.routeAreas[i].new) {
            this.$emit('input', true);
            return true;
          }
        }
      }
      this.$emit('input', false);
      return false;
    },
  },
  methods: {
    ...mapActions({
      loadRouteAreas: 'transportation/routesLoadAreas',
      saveRoute: 'transportation/routesSaveAreas',
      clearRoute: 'transportation/routesClearRoute',
      addArea: 'transportation/routesAddArea',
      removeArea: 'transportation/routesRemoveArea',
      addAllAreas: 'transportation/routesAddAllAreas',
      removeAllAreas: 'transportation/routesRemoveAllAreas',
      loadCountries: 'twenty/countriesLoad',
      loadAreas: 'twenty/areasLoad',
      clearAreas: 'twenty/areasClear',
    }),
    refreshAreas() {
      this.loadAreas(this.country);
    },
    refreshRoute() {
      this.loadRouteAreas();
    },
    refreshCountries() {
      if (this.countriesError) {
        this.loadCountries();
      }
    },
    addAll() {
      this.addAllAreas(this.sortedAreas);
    },
    removeAll() {
      this.removeAllAreas(this.sortedRouteAreas);
    },
    addMapping(area) {
      if (!this.loading) {
        this.addArea(area);
      }
    },
    removeMapping(area) {
      if (!this.loading) {
        this.removeArea(area);
      }
    },
    saveMapping() {
      this.saveRoute()
        .then((res) => {
          if (res) {
            this.$emit('close', true);
          }
        });
    },
  },
  data() {
    return {
      country: undefined,
      search: '',
      current: undefined,
    };
  },
  watch: {
    shown() {
      if (this.shown) {
        if (!this.routeAreas.length || !this.route || this.route.id !== this.current) {
          this.loadRouteAreas();
          this.clearAreas();
          this.country = undefined;
          this.current = this.route.id;
        }
        if (!this.countriesList.length) {
          this.loadCountries();
        }
        this.$emit('input', this.changesMade);
      }
    },
  },
};
</script>

<template>
  <v-card class="ferry-route-mapping-wrapper" v-if="shown">
    <v-card-title>
      <span class="headline">
        {{ $t('transportation.routes.actions.edit.title') }}
      </span>
    </v-card-title>

    <v-card-text class="py-0 mb-3 edit-item-status">
      <v-progress-linear
        class="my-0"
        v-if="saveLoading"
        :active="saveLoading"
        :indeterminate="true"
        :height="4"
      />
      <v-alert
        class="my-0 py-2"
        outline
        v-if="!saveLoading"
        :value="!!saveError"
        transition="slide-y-transition">
        {{ $t('errors.saveError') }}
      </v-alert>
    </v-card-text>

    <v-card-text class="py-0">
      <v-container grid-list-md>
        <v-layout row justify-space-between>
          <v-flex xs5>
            <v-card-text class="py-0">
              <v-text-field
                v-model="search"
                clearable
                solo
                hide-details
                :placeholder="$t('inputs.placeholders.search')"
              />
            </v-card-text>
          </v-flex>
          <v-flex xs5>
            <v-card-text class="py-0">
              <v-select
                v-model="country"
                :items="countriesList"
                :loading="countriesLoading"
                :label="$t('twenty.countries.title')"
                :disabled="countriesLoading || !!routeAreasError || routeAreasLoading"
                :error-messages="countriesError ? $t('twenty.countries.error') : undefined"
                :error="!!countriesError"
                :append-icon="!countriesError ? 'filter_list' : 'refresh'"
                @input="refreshAreas"
                @click:append="refreshCountries"
                item-value="id"
                item-text="name"
                dense
                solo
              />
            </v-card-text>
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs5>
            <v-card-text class="pt-0">
              <v-card
                class="ferry-routes__edit-areas"
                :class="{ 'overflow-y-hidden': !routeAreas }">
                <v-list class="py-0" dense>
                  <v-progress-linear
                    class="my-0"
                    v-if="routeAreasLoading"
                    :indeterminate="true"
                  />
                  <v-list-tile v-else-if="routeAreasError">
                    <v-list-tile-title class="text-xs-center">
                      {{ $t('transportation.routes.actions.edit.routeError') }}.
                      <span
                        class="refresh-text"
                        @click="refreshRoute">
                          {{ $t('inputs.actions.reload') }}?
                        </span>
                    </v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile v-else-if="!sortedRouteAreas.length">
                    <v-list-tile-title class="text-xs-center">
                      {{ $t('transportation.routes.actions.edit.noMappedAreas') }}
                    </v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile
                    class="ferry-routes__edit-areas-item"
                    :class="{
                      'red lighten-5': area.deleted,
                      'green lighten-5': area.new,
                    }"
                    v-for="area in sortedRouteAreas"
                    :key="area.id"
                    @click="removeMapping(area)">
                    <v-list-tile-title>
                      {{ area.name }}
                      <span v-if="countries[area.countryId]" class="caption font-italic grey--text">
                        ({{ countries[area.countryId].name }})
                      </span>
                    </v-list-tile-title>
                  </v-list-tile>
                </v-list>
              </v-card>
            </v-card-text>
            <v-card-actions class="pr-3 pt-0">
              <v-spacer/>
              <v-btn
                :disabled="loading || !sortedRouteAreas.length"
                @click="removeAll"
                color="primary">
                {{ $t('transportation.routes.actions.edit.removeAll') }}
              </v-btn>
            </v-card-actions>
          </v-flex>
          <v-flex xs2 class="text-xs-center pb-3">
            <v-divider light vertical/>
          </v-flex>
          <v-flex xs5>
            <v-card-text class="pt-0">
              <v-card
                class="ferry-routes__edit-areas"
                :class="{ 'overflow-y-hidden': !country || !areas }">
                <v-list class="py-0" dense>
                  <v-list-tile v-if="!country">
                    <v-list-tile-title class="text-xs-center">
                      {{ $t('transportation.routes.actions.edit.selectCountry') }}
                    </v-list-tile-title>
                  </v-list-tile>
                  <v-progress-linear
                    class="my-0"
                    v-else-if="areasLoading"
                    :indeterminate="true"
                  />
                  <v-list-tile v-else-if="areasError">
                    <v-list-tile-title class="text-xs-center">
                      {{ $t('transportation.routes.actions.edit.areasError') }}.
                      <span
                        class="refresh-text"
                        @click="refreshAreas">
                          {{ $t('inputs.actions.reload') }}?
                        </span>
                    </v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile v-else-if="!sortedAreas.length">
                    <v-list-tile-title class="text-xs-center">
                      {{ $t('transportation.routes.actions.edit.noAreasAvailable') }}
                    </v-list-tile-title>
                  </v-list-tile>
                  <v-list-tile
                    class="ferry-routes__edit-areas-item"
                    v-for="area in sortedAreas"
                    :key="area.id"
                    @click="addMapping(area)">
                    <v-list-tile-title>
                      {{ area.name }}
                    </v-list-tile-title>
                  </v-list-tile>
                </v-list>
              </v-card>
            </v-card-text>

            <v-card-actions class="pr-3 pt-0">
              <v-spacer/>
              <v-btn
                :disabled="loading || !sortedAreas.length"
                @click="addAll"
                color="primary">
                {{ $t('transportation.routes.actions.edit.addAll') }}
              </v-btn>
            </v-card-actions>
          </v-flex>
        </v-layout>
      </v-container>
    </v-card-text>

    <v-card-actions>
      <v-spacer/>
      <v-btn
        @click="$emit('close')"
        :disabled="loading"
        color="blue darken-1"
        flat>
        {{ $t('inputs.actions.cancel') }}
      </v-btn>
      <v-tooltip :disabled="changesMade" top>
        <template slot="activator" slot-scope="{ on }">
          <div v-on="on">
            <v-btn
              @click="saveMapping"
              :disabled="loading || !changesMade"
              color="blue darken-1"
              flat>
              {{ $t('inputs.actions.save') }}
            </v-btn>
          </div>
        </template>
        <span>
          {{ $t('inputs.misc.saveDisabled') }}
        </span>
      </v-tooltip>
    </v-card-actions>
  </v-card>
</template>

<style lang="scss">
  .ferry-routes {
    &__edit-areas {
      overflow-y: scroll;
      height: 40vh;

      &-item {
        cursor: pointer;
      }
    }
  }

  .edit-item-status {
    min-height: 5px;
  }

  .overflow-y-hidden {
    overflow-y: hidden !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/transportation/TransportationRoutesMapping.vue